/**
 * Adds a vertical separator line between years (e.g., between 2024-12 and 2025-01)
 * in an ECharts configuration.
 *
 * This function:
 * 1. Inserts a separator marker ('|') in the xAxis data at the year boundary
 * 2. Updates all existing series to include undefined at the separator position
 * 3. Creates an invisible line series with a markLine positioned at the separator marker
 *
 * Why a separate line series instead of markLine on bar series?
 * - markLine on bar series is affected by barWidth and barGap, causing misalignment
 * - Line series use the xAxis coordinate system directly, ensuring perfect alignment
 * - The invisible line series provides a coordinate system independent of bar positioning
 *
 * The API always returns 24 months: 12 from the previous year + 12 from the current year,
 * so the year boundary is always at index 12. As a fallback, we detect the boundary
 * dynamically by comparing the year of adjacent xAxis dates.
 *
 * @param opt - ECharts option object to modify
 * @param separatorColor - Color of the separator line (default: '#7F6FF1')
 * @returns The modified option object
 */
export const addYearSeparator = (opt: any, separatorColor: string = '#7F6FF1'): any => {
  if (!opt.series || opt.series.length === 0) {
    return opt;
  }

  const separatorMarker = '|';
  const xAxisData = opt.xAxis?.data || [];

  // Idempotency guard:
  // setOtherConfigFunc can run multiple times (e.g. legend toggle), so avoid
  // repeatedly injecting separator marker/series and creating dummy data slots.
  const hasSeparatorMarker = Array.isArray(xAxisData) && xAxisData.includes(separatorMarker);
  const hasSeparatorSeries = Array.isArray(opt.series)
    && opt.series.some((s: any) => s?.name === '__year_separator_line__');
  if (hasSeparatorMarker || hasSeparatorSeries) {
    return opt;
  }

  // Find year boundary dynamically by detecting where the year changes between adjacent dates.
  // Fallback to index 12 since API always returns 12 prev-year + 12 current-year months.
  let boundaryIndex = -1;
  for (let i = 1; i < xAxisData.length; i++) {
    const prevYear = xAxisData[i - 1]?.substring(0, 4);
    const currYear = xAxisData[i]?.substring(0, 4);
    if (prevYear && currYear && prevYear !== currYear) {
      boundaryIndex = i;
      break;
    }
  }

  // Fallback: API always returns 24 months (12 + 12), boundary at index 12
  if (boundaryIndex <= 0 && xAxisData.length === 24) {
    boundaryIndex = 12;
  }

  if (boundaryIndex <= 0) {
    return opt; // No year boundary found or already at start
  }

  // Insert a separator marker in xAxis data at the year boundary
  opt.xAxis.data = [
    ...xAxisData.slice(0, boundaryIndex),
    separatorMarker,
    ...xAxisData.slice(boundaryIndex)
  ];

  // Hide the separator label in xAxis (it's just a positioning marker)
  const originalFormatter = opt.xAxis.axisLabel?.formatter;
  opt.xAxis.axisLabel = {
    ...opt.xAxis.axisLabel,
    formatter: (value: string) => {
      if (value === separatorMarker) {
        return ''; // Hide the separator label
      }
      return typeof originalFormatter === 'function' ? originalFormatter(value) : value;
    }
  };

  // Update all existing series to include undefined at the separator position
  // This ensures data alignment with the new xAxis structure
  opt.series = opt.series.map((series: any) => {
    if (series.data && Array.isArray(series.data)) {
      return {
        ...series,
        data: [
          ...series.data.slice(0, boundaryIndex),
          undefined, // No data at separator position
          ...series.data.slice(boundaryIndex)
        ]
      };
    }
    return series;
  });

  // Create data array for the invisible line series
  // All positions are undefined except the separator marker position (null)
  const separatorLineData = new Array(opt.xAxis.data.length).fill(undefined);
  separatorLineData[boundaryIndex] = null; // Separator marker position

  // Add invisible line series with markLine for the separator
  // This series is independent of bar positioning, ensuring accurate alignment
  opt.series.push({
    name: '__year_separator_line__',
    type: 'line',
    data: separatorLineData,
    hiddenFromLegend: true,
    silent: true,
    lineStyle: {
      opacity: 0, // Invisible line (we only need the markLine)
    },
    symbol: 'none',
    tooltip: {
      show: false, // Exclude from tooltip
    },
    markLine: {
      silent: true,
      symbol: 'none',
      animation: false,
      label: {
        show: false
      },
      data: [
        {
          xAxis: separatorMarker, // Position at the separator marker category
          lineStyle: {
            color: separatorColor,
            type: 'dashed',
            width: 2,
          }
        }
      ]
    },
    z: 100, // High z-index to ensure separator appears on top
  });

  return opt;
};

const getLegendSelectedMap = (opt: any): Record<string, boolean> => {
  // ECharts legend can be object or array form; normalize once for downstream logic.
  return (Array.isArray(opt?.legend)
    ? opt?.legend?.[0]?.selected
    : opt?.legend?.selected) || {};
};

export const getVisibleStackContext = (
  opt: any,
  metricKeys: string[] = ['studio', 'publishing', 'casual'],
) => {
  // Runtime legend state is required here because users can hide/show series interactively.
  // Label placement should follow the "currently visible" stack, not the raw full dataset.
  const legendSelected = getLegendSelectedMap(opt);
  const visibleStackSeries = (opt?.series || []).filter((series: any) => {
    if (series?.type !== 'bar') {
      return false;
    }
    const currentSeriesName = String(series?.name || '').toLowerCase();
    const isStackMetric = metricKeys.some(metric => currentSeriesName.includes(metric));
    if (!isStackMetric) {
      return false;
    }
    // ECharts sets selected[name] = false when the legend item is hidden.
    return legendSelected?.[series?.name] !== false;
  });

  // Compute max stack height among visible series only.
  // This value is used as a dynamic scale reference for adaptive label positioning.
  const maxVisibleStackTotal = Array.from({
    length: visibleStackSeries.reduce((maxLen: number, series: any) => {
      const dataLength = Array.isArray(series?.data) ? series.data.length : 0;
      return Math.max(maxLen, dataLength);
    }, 0),
  }).reduce((maxTotal: number, _unused, index: number) => {
    const stackTotalAtIndex = visibleStackSeries.reduce((sum: number, series: any) => {
      const rawValue = series?.data?.[index];
      const numericValue = Number(rawValue?.value ?? rawValue);
      if (!Number.isFinite(numericValue)) {
        return sum;
      }
      // This helper is for stacked bar "height" context only, so negative values
      // should not reduce available upward space for inside labels.
      return sum + Math.max(numericValue, 0);
    }, 0);
    return Math.max(maxTotal, stackTotalAtIndex);
  }, 0);

  return {
    legendSelected,
    visibleStackSeries,
    visibleStackedMetricSeriesCount: visibleStackSeries.length,
    maxVisibleStackTotal,
  };
};

export const buildAdaptiveCasualLabelData = (
  seriesData: any[],
  {
    maxVisibleStackTotal,
    labelFormatter,
    ratioThreshold = 0.08,
  }: {
    maxVisibleStackTotal: number;
    labelFormatter: (value: number) => string;
    ratioThreshold?: number;
  },
) => {
  // Per-point adaptation is intentional:
  // different months can have very different casual magnitudes.
  return (Array.isArray(seriesData) ? seriesData : []).map((item: any) => {
    const itemData =
      typeof item === 'object' && item !== null
        ? item
        : { value: item };
    const casualValueRaw = itemData?.casual ?? itemData?.value;
    const casualValue = Number(casualValueRaw);
    // Casual label rules:
    // 1) casual === 0: always show plain "0" at top (user requirement).
    // 2) casual > 0 but tiny compared with current visible max stack:
    //    move label to top to avoid clipped/overlapped inside text.
    // 3) otherwise: keep label inside bar.
    const shouldPinToTop =
      (Number.isFinite(casualValue) && casualValue === 0)
      || (
        Number.isFinite(casualValue)
        && casualValue > 0
        && Number.isFinite(maxVisibleStackTotal)
        && maxVisibleStackTotal > 0
        && casualValue / maxVisibleStackTotal <= ratioThreshold
      );

    return {
      ...itemData,
      label: {
        show: true,
        position: shouldPinToTop ? 'top' : 'inside',
        // Add a tiny offset only when pinned to top for clearer separation.
        distance: shouldPinToTop ? 2 : 0,
        fontSize: 12,
        fontWeight: 'normal',
        color: '#333',
        formatter:
          Number.isFinite(casualValue)
            ? (casualValue === 0 ? '0' : labelFormatter(casualValue))
            : '',
      },
    };
  });
};

export const processIEGGFinancialsData = (dataList: any) => {
  const flattenNode = (node: any, depth: number = 0): any => {
    const { gross_revenue, profit, children, ...rest } = node;
    const flat: any = { ...rest, _depth: depth };
    // gross_revenue 扁平化: actual -> actual_gross_revenue
    if (gross_revenue && typeof gross_revenue === 'object') {
      Object.keys(gross_revenue).forEach((key) => {
        flat[`${key}_gross_revenue`] = gross_revenue[key];
      });
    }
    // profit 扁平化: actual -> actual_profit
    if (profit && typeof profit === 'object') {
      Object.keys(profit).forEach((key) => {
        flat[`${key}_profit`] = profit[key];
      });
    }
    // 递归处理 children
    if (Array.isArray(children) && children.length > 0) {
      flat.children = children.map((child: any) => flattenNode(child, depth + 1));
    }
    return flat;
  };

  if (!dataList || !Array.isArray(dataList)) return [];
  return dataList.map((node: any) => flattenNode(node, 0));
};

export const downloadInfoMap = {
  yearData: {
    downloadLogKey: 'financialsThisYearDataDownload',
    downloadServiceApi: '/api/v1/mgmt_pc/business/iegg_financial_dashboard/download',
    fileName: `IEGG Financials Dashboard - This Year's Data.xlsx`,
  },
  detailedData: {
    downloadLogKey: 'financialsAllDetailedDataDownload',
    downloadServiceApi: '/api/v1/mgmt_pc/business/iegg_financial_all_detail/download',
    fileName: `IEGG Financials Dashboard - All Detailed Data.xlsx`,
  },
  seriesData: {
    downloadLogKey: 'financialsTimeSeriesDataDownload',
    downloadServiceApi: '/api/v1/mgmt_pc/business/iegg_financial_time_series/download',
    fileName: `IEGG Financials Dashboard - Time Series Data.xlsx`,
  },
};
